import React, { useCallback, useEffect, useRef, useState } from "react";
import { Divider, FormHelperText, Radio, Typography } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import { TextField } from "@components/Input/Input.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useTranslation } from "react-i18next";
import { getUploadEntryName } from "../attachmentUtils";
import { interruptSchema } from "../interrupt/interruptSchema";

interface RejectResumptionDrawerProps {
  isOpen: boolean;
  internalAttachmentUrl?: string;
  internalAttachmentName?: string;
  externalAttachmentUrl?: string;
  externalAttachmentName?: string;
  onDocumentUpload?: (file: File) => void;
  onAdditionalDocumentUpload?: (file: File) => void;
  onClose: (drawerName: string) => void;
  onSubmit?: (payload?: any) => void;
}

const RejectResumptionDrawer: React.FC<RejectResumptionDrawerProps> = ({
  isOpen,
  internalAttachmentUrl,
  internalAttachmentName,
  externalAttachmentUrl,
  externalAttachmentName,
  onClose,
  onDocumentUpload,
  onAdditionalDocumentUpload,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const LANG_BASE = "vessel-call";

  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const TEXTAREA_MAX_CHARS = 1000;
  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };
  const [externalNotes, setExternalNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [forceLeave, setForceLeave] = useState<boolean>(false);
  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
  const fileProfiles = useFileUploadProfiles();
  const externalProfile = fileProfiles.sideDrawerApprovalFile;
  const internalProfile = externalProfile;
  const detailsSchema = interruptSchema(t);
  const externalOnlySchema = interruptSchema(t).pick({ externalNotes: true });
  const internalOnlySchema = interruptSchema(t).pick({ internalNotes: true });

  const [errors, setErrors] = useState<{
    forceLeave?: string;
    externalNotes?: string;
    internalNotes?: string;
    externalFile?: string;
    internalFile?: string;
  }>({});
  type ErrorsType = {
    forceLeave?: string;
    externalNotes?: string;
    internalNotes?: string;
    externalFile?: string;
    internalFile?: string;
  };
  const allowedExternalExtensions = externalProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );
  const allowedInternalExtensions = internalProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );
  const externalFiles = useUploadStore(
    (s) => s.filesByKey.ExternalAttachment ?? EMPTY_UPLOAD_ENTRIES,
  );
  const internalFiles = useUploadStore(
    (s) => s.filesByKey.InternalAttachment ?? EMPTY_UPLOAD_ENTRIES,
  );
  const externalUploadSignatureRef = useRef<string | null>(null);
  const internalUploadSignatureRef = useRef<string | null>(null);

  const setFilesForKey = useUploadStore((s) => s.setFilesForKey);
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);
  const removeInternalFile = () => {
    clearFilesForKey("InternalAttachment");
  };
  const removeExternalFile = () => {
    clearFilesForKey("ExternalAttachment");
  };
  const clearFilesAndNotes = useCallback(() => {
    removeInternalFile();
    removeExternalFile();
    setExternalNotes("");
    setInternalNotes("");
  }, [clearFilesForKey]);

  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };

  useEffect(() => {
    if (!isOpen) {
      clearFilesAndNotes();
      setErrors({});
      setForceLeave(false);
    }
  }, [isOpen]);

  const setExternalAttachment = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("ExternalAttachment");
        return;
      }
      setFilesForKey("ExternalAttachment", files);
    },
    [clearFilesForKey, setFilesForKey],
  );
  const setInternalAttachment = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("InternalAttachment");
        return;
      }
      setFilesForKey("InternalAttachment", files);
    },
    [clearFilesForKey, setFilesForKey],
  );
  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.MutableRefObject<string | null>,
    ) => {
      if (!handler) return;
      if (!(entry instanceof File)) return;

      const signature = getFileSignature(entry);
      if (!signature) return;

      if (tracker.current === signature) return;

      tracker.current = signature;
      handler(entry);
    },
    [],
  );
  const handleFileUpload = ({
    newFiles,
    setUploadErrors,
    setUploadFiles,
    onUploadDocument,
    uploadTracker,
  }: {
    newFiles: File[];
    setUploadErrors: (
      value:
        | { externalFile?: string; internalFile?: string }
        | ((prev: { externalFile?: string; internalFile?: string }) => {
            externalFile?: string;
            internalFile?: string;
          }),
    ) => void;
    setUploadFiles: (files: UploadEntry[]) => void;
    onUploadDocument?: (file: File) => void;
    uploadTracker: React.MutableRefObject<string | null>;
  }) => {
    if (newFiles.length > 1) {
      setUploadErrors({ externalFile: t("upload.one-document") });
      return;
    }

    const file = newFiles[0];
    if (!file) {
      return;
    }

    setUploadErrors((prev) => ({ ...prev, files: undefined }));
    setUploadFiles([file]);
    triggerUpload(file, onUploadDocument, uploadTracker);
  };
  const handleSubmit = () => {
    const toValidate = {
      forceLeave: forceLeave as boolean,
      externalNotes,
    };

    const validation = interruptSchema(t).safeParse(toValidate);
    if (!validation.success) {
      const flat = validation.error.flatten().fieldErrors;
      setErrors((prev) => ({
        ...prev,
        forceLeave: flat.forceLeave?.[0],
        externalNotes: flat.externalNotes?.[0],
      }));
      return;
    }

    const payload = {
      forceLeave: forceLeave as boolean,
      externalNotes,
      internalNotes,
      internalAttachmentUrl,
      internalAttachmentName:
        getUploadEntryName(internalFiles[0], internalAttachmentName) || undefined,
      externalAttachmentUrl,
      externalAttachmentName:
        getUploadEntryName(externalFiles[0], externalAttachmentName) || undefined,
    };
    onSubmit?.(payload);
    setForceLeave(false);
  };

  return (
    <div data-testid="reject-resumption-drawer">
      <SideDrawer
        isOpen={isOpen}
        title={t(`${LANG_BASE}.resumption.reject-title`)}
        onClose={() => {
          clearFilesAndNotes();
          setErrors({});
          onClose("rejectResumptionDrawer");
        }}
        submitLabel={t(`${LANG_BASE}.submit`, { defaultValue: "Reject" })}
        onSubmit={handleSubmit}
      >
        <div className="max-w-sm">
          <Typography
            variant="body1"
            data-testid="interrupt-vessel-call-title"
            sx={{ color: "text.primary", px: 3, pb: 3 }}
          >
            {t("vessel-call.resumption.reject-subtitle")}
          </Typography>
          <div className="flex-1 max-w-sm py-2">
            <div className="flex flex-col gap-4 px-6">
              {/* Details */}
              <div className="group">
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t(`${LANG_BASE}.interrupt.force-leave`)}
                </Typography>
                <div className="flex flex-col gap-1">
                  <div className="flex gap-8 items-center">
                    <div className="group">
                      <Radio
                        size="small"
                        checked={forceLeave === true}
                        onChange={() => {
                          const val = true;
                          setForceLeave(val);
                          const res = detailsSchema.safeParse({ forceLeave: val });
                          if (!res.success) {
                            const flat = res.error.flatten().fieldErrors;
                            setErrors((prev) => ({ ...prev, forceLeave: flat.forceLeave?.[0] }));
                          } else {
                            setErrors((prev) => ({ ...prev, forceLeave: undefined }));
                          }
                        }}
                        onBlur={() => {
                          const res = detailsSchema.safeParse({ forceLeave });
                          if (!res.success) {
                            const flat = res.error.flatten().fieldErrors;
                            setErrors((prev) => ({ ...prev, forceLeave: flat.forceLeave?.[0] }));
                          } else {
                            setErrors((prev) => ({ ...prev, forceLeave: undefined }));
                          }
                        }}
                        aria-label="force-leave-yes"
                      />
                      <span>{t("common.yes")}</span>
                    </div>
                    <div className="group">
                      <Radio
                        size="small"
                        checked={forceLeave === false}
                        onChange={() => {
                          const val = false;
                          setForceLeave(val);
                          const res = detailsSchema.safeParse({ forceLeave: val });
                          if (!res.success) {
                            const flat = res.error.flatten().fieldErrors;
                            setErrors((prev) => ({ ...prev, forceLeave: flat.forceLeave?.[0] }));
                          } else {
                            setErrors((prev) => ({ ...prev, forceLeave: undefined }));
                          }
                        }}
                        onBlur={() => {
                          const res = detailsSchema.safeParse({ forceLeave });
                          if (!res.success) {
                            const flat = res.error.flatten().fieldErrors;
                            setErrors((prev) => ({ ...prev, forceLeave: flat.forceLeave?.[0] }));
                          } else {
                            setErrors((prev) => ({ ...prev, forceLeave: undefined }));
                          }
                        }}
                        aria-label="force-leave-no"
                      />
                      <span>{t("common.no")}</span>
                    </div>
                  </div>
                  {errors.forceLeave && (
                    <FormHelperText sx={{ color: "error.main", m: 0 }}>
                      {errors.forceLeave}
                    </FormHelperText>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 " data-testid="external-notes">
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t(`${LANG_BASE}.common.external-notes`)}
                </Typography>
                <TextField
                  multiline
                  minRows={4}
                  value={externalNotes}
                  placeholder={t(`${LANG_BASE}.common.external-notes`)}
                  sx={{ "& textarea": { padding: "12px", borderRadius: "8px" } }}
                  inputProps={{ maxLength: TEXTAREA_MAX_CHARS }}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (!isPrintableKey(e)) return;
                    if (externalNotes.length >= TEXTAREA_MAX_CHARS) {
                      setErrors((prev) => ({
                        ...prev,
                        externalNotes: t("form-validation.max-length", {
                          label: t(`${LANG_BASE}.common.external-notes`),
                          max: TEXTAREA_MAX_CHARS,
                        }),
                      }));
                    }
                  }}
                  onBlur={() => {
                    const res = externalOnlySchema.safeParse({ externalNotes });
                    if (!res.success) {
                      const flat = res.error.flatten().fieldErrors;
                      setErrors((prev: ErrorsType) => ({
                        ...prev,
                        externalNotes: flat.externalNotes?.[0],
                      }));
                    } else {
                      setErrors((prev: ErrorsType) => ({ ...prev, externalNotes: undefined }));
                    }
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    setExternalNotes(val);
                    const res = externalOnlySchema.safeParse({ externalNotes: val });
                    if (!res.success) {
                      const flat = res.error.flatten().fieldErrors;
                      setErrors((prev) => ({ ...prev, externalNotes: flat.externalNotes?.[0] }));
                    } else {
                      setErrors((prev) => ({ ...prev, externalNotes: undefined }));
                    }
                  }}
                />
                {errors.externalNotes && (
                  <FormHelperText sx={{ color: "error.main", m: 0 }}>
                    {errors.externalNotes}
                  </FormHelperText>
                )}
                <FormHelperText sx={charCounterSx}>
                  {externalNotes.length}/{TEXTAREA_MAX_CHARS}
                </FormHelperText>
                <DocumentUpload
                  label={t(`${LANG_BASE}.common.external-attachments`)}
                  testId="external-attachment-upload"
                  name="ExternalAttachment"
                  maxFiles={1}
                  allowedExtensions={allowedExternalExtensions}
                  onUpload={(newFiles) =>
                    handleFileUpload({
                      newFiles,
                      setUploadErrors: setErrors,
                      setUploadFiles: setExternalAttachment,
                      onUploadDocument: onDocumentUpload,
                      uploadTracker: externalUploadSignatureRef,
                    })
                  }
                  conditions={externalProfile}
                  files={externalFiles}
                  setFiles={(next) => setFilesForKey("ExternalAttachment", next)}
                  uploadDescription={t("upload.dragOrDropDescription")}
                />
                {errors.externalFile && (
                  <FormHelperText sx={{ color: "error.main", m: 0 }}>
                    {errors.externalFile}
                  </FormHelperText>
                )}
                {externalFiles[0] && (
                  <SingleFile
                    file={externalFiles[0]}
                    fieldName="ExternalAttachment"
                    setFiles={() => {
                      setErrors((prev) => ({ ...prev, files: undefined }));
                      setExternalAttachment([]);
                      externalUploadSignatureRef.current = null;
                    }}
                  />
                )}
              </div>
              <Divider />
              {/* Internal Notes */}
              <div className="flex flex-col gap-2" data-testid="internal-notes">
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t(`${LANG_BASE}.common.internal-notes`)}
                </Typography>
                <TextField
                  multiline
                  minRows={4}
                  value={internalNotes}
                  inputProps={{ maxLength: TEXTAREA_MAX_CHARS }}
                  placeholder={t(`${LANG_BASE}.common.internal-notes`)}
                  sx={{ "& textarea": { padding: "12px", borderRadius: "8px" } }}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (!isPrintableKey(e)) return;
                    if (internalNotes.length >= TEXTAREA_MAX_CHARS) {
                      setErrors((prev) => ({
                        ...prev,
                        internalNotes: t("form-validation.max-length", {
                          label: t(`${LANG_BASE}.common.internal-notes`),
                          max: TEXTAREA_MAX_CHARS,
                        }),
                      }));
                    }
                  }}
                  onBlur={() => {
                    const res = internalOnlySchema.safeParse({ internalNotes });
                    if (!res.success) {
                      const flat = res.error.flatten().fieldErrors;
                      setErrors((prev: ErrorsType) => ({
                        ...prev,
                        internalNotes: flat.internalNotes?.[0],
                      }));
                    } else {
                      setErrors((prev: ErrorsType) => ({ ...prev, internalNotes: undefined }));
                    }
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    setInternalNotes(val);
                    const res = internalOnlySchema.safeParse({ internalNotes: val });
                    if (!res.success) {
                      const flat = res.error.flatten().fieldErrors;
                      setErrors((prev) => ({ ...prev, internalNotes: flat.internalNotes?.[0] }));
                    } else {
                      setErrors((prev) => ({ ...prev, internalNotes: undefined }));
                    }
                  }}
                />
                {errors.internalNotes && (
                  <FormHelperText sx={{ color: "error.main", m: 0 }}>
                    {errors.internalNotes}
                  </FormHelperText>
                )}
                <FormHelperText sx={charCounterSx}>
                  {internalNotes.length}/{TEXTAREA_MAX_CHARS}
                </FormHelperText>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t(`${LANG_BASE}.common.internal-attachments`)}
                </Typography>
                <DocumentUpload
                  testId="internal-attachment-upload"
                  onUpload={(newFiles) =>
                    handleFileUpload({
                      newFiles,
                      setUploadErrors: setErrors,
                      setUploadFiles: setInternalAttachment,
                      onUploadDocument: onAdditionalDocumentUpload,
                      uploadTracker: internalUploadSignatureRef,
                    })
                  }
                  name="InternalAttachment"
                  conditions={internalProfile}
                  files={internalFiles}
                  setFiles={(next) => setFilesForKey("InternalAttachment", next)}
                  maxFiles={1}
                  allowedExtensions={allowedInternalExtensions}
                  uploadDescription={t("upload.dragOrDropDescription")}
                />
                {errors.internalFile && (
                  <FormHelperText sx={{ color: "error.main", m: 0 }}>
                    {errors.internalFile}
                  </FormHelperText>
                )}
                {internalFiles[0] && (
                  <SingleFile
                    file={internalFiles[0]}
                    testId="internal-attachment"
                    fieldName="InternalAttachment"
                    currentFileIndex={0}
                    setFiles={() => {
                      setErrors((prev: ErrorsType) => ({ ...prev, files: undefined }));
                      setInternalAttachment([]);
                      internalUploadSignatureRef.current = null;
                    }}
                    removeFn={removeInternalFile}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default RejectResumptionDrawer;
